/**
 * Switch Core Types
 *
 * Re-exports auto-generated types from Rust schemas and provides
 * runtime enum objects for convenience.
 *
 * Types are generated by ts-rs from switch-core Rust crate.
 * Run `cargo test` in switch-core to regenerate.
 */

// ============================================================================
// Re-export all generated types
// ============================================================================

import { WebSocketEventTypeValues } from "./bindings";
import type { WebSocketEventType as WebSocketEventTypeFromBindings } from "./bindings";
export type { ProfileMediaAsset } from "./bindings/ProfileMediaAsset";

export type {
  User,
  UserForClient,
  UserType,
  PublicKeyEntry,
  Contact,
  AddContactResult,
  Member,
  MemberRole,
  MembershipStatus,
  MembershipStatusType,
  Message,
  MessageType as MessageTypeEnum,
  MessageStatus as MessageStatusType,
  ContentType as ContentTypeEnum,
  Attachment,
  AttachmentMimeType as AttachmentMimeTypeEnum,
  Environment as EnvironmentEnum,
  ThreadType as ThreadTypeEnum,
  GroupUpdatedMessageContent,
  GroupUpdateReason as GroupUpdateReasonEnum,
  CommunityUpdatedMessageContent,
  CommunityUpdateReason as CommunityUpdateReasonEnum,
  Thread,
  HiloMeta,
  EncryptedThread,
  Community,
  CommunityGroup,
  EncryptedCommunity,
  Token,
  TokenType,
  PushToken,
  PushPlatform as PushPlatformEnum,
  DebugAppScreen,
  WebSocketEventType as WebSocketEventTypeEnum,
} from "./bindings";

// ============================================================================
// Runtime Enum Objects - for creating values at runtime
// ============================================================================

/** Content type for messages */
export const ContentType = {
  TEXT: "TEXT",
  IMAGE: "IMAGE",
  VIDEO: "VIDEO",
  AUDIO: "AUDIO",
  FILE: "FILE",
  SYSTEM: "SYSTEM",
  GIPHY: "GIPHY",
  ATTACHMENT: "ATTACHMENT",
} as const;
export type ContentType = (typeof ContentType)[keyof typeof ContentType];

/** Message type enum - matches Rust MessageType (canonical names) */
export const MessageType = {
  // Core canonical types
  CALL: "CALL",
  HANGUP: "HANGUP",
  ANSWER: "ANSWER",
  MESSAGE: "MESSAGE",
  SYSTEM: "SYSTEM",
  THREAD_KEY: "THREAD_KEY",
  EXIT_THREAD: "EXIT_THREAD",
  COMMUNITY_MEMBER_ADDED: "COMMUNITY_MEMBER_ADDED",
  COMMUNITY_MEMBER_REMOVED: "COMMUNITY_MEMBER_REMOVED",
  THREAD_MEMBER_ADDED: "THREAD_MEMBER_ADDED",
  THREAD_MEMBER_REMOVED: "THREAD_MEMBER_REMOVED",
  DELETE_COMMUNITY: "DELETE_COMMUNITY",
  EXIT_COMMUNITY: "EXIT_COMMUNITY",
  COMMUNITY_CREATED: "COMMUNITY_CREATED",
  COMMUNITY_UPDATED: "COMMUNITY_UPDATED",
  THREAD_CREATED: "THREAD_CREATED",
  THREAD_UPDATED: "THREAD_UPDATED",
  THREAD_DELETED: "THREAD_DELETED",
  COMMUNITY_DELETED: "COMMUNITY_DELETED",
  COMMUNITY_THREAD_JOIN_REQUEST: "COMMUNITY_THREAD_JOIN_REQUEST",
  COMMUNITY_THREAD_UPDATED: "COMMUNITY_THREAD_UPDATED",
  CALL_JOINED: "CALL_JOINED",
  USER_ADDED: "USER_ADDED",
} as const;
export type MessageType = (typeof MessageType)[keyof typeof MessageType];

// ============================================================================
// Message Type Helper - matches iOS/Android MessageTypeHelper
// ============================================================================

/** System message types (not regular user content) */
const SYSTEM_MESSAGE_TYPES = new Set([
  "THREAD_KEY",
  "THREAD_CREATED",
  "THREAD_UPDATED",
  "THREAD_DELETED",
  "THREAD_MEMBER_ADDED",
  "THREAD_MEMBER_REMOVED",
  "EXIT_THREAD",
  "USER_ADDED",
  "COMMUNITY_CREATED",
  "COMMUNITY_UPDATED",
  "COMMUNITY_DELETED",
  "COMMUNITY_MEMBER_ADDED",
  "COMMUNITY_MEMBER_REMOVED",
]);

/**
 * Message type helper utilities - matches iOS MessageTypeHelper and Android CoreTypeConverters.
 * Provides consistent message type handling across all platforms.
 */
export const MessageTypeHelper = {
  /**
   * Canonicalize a message type string using core's mapping.
   */
  canonicalize(typeStr: string | null | undefined): string | null {
    if (!typeStr) return null;
    return typeStr.toUpperCase();
  },

  /** Check if a message type is a thread/group created message */
  isThreadCreated(typeStr: string | null | undefined): boolean {
    const canonical = MessageTypeHelper.canonicalize(typeStr);
    return canonical === "THREAD_CREATED";
  },

  /** Check if a message type is a thread/group updated message */
  isThreadUpdated(typeStr: string | null | undefined): boolean {
    const canonical = MessageTypeHelper.canonicalize(typeStr);
    return canonical === "THREAD_UPDATED";
  },

  /** Check if a message type is a thread key message */
  isThreadKey(typeStr: string | null | undefined): boolean {
    const canonical = MessageTypeHelper.canonicalize(typeStr);
    return canonical === "THREAD_KEY";
  },

  /** Check if a message type is a system/control message (not regular content) */
  isSystemMessage(typeStr: string | null | undefined): boolean {
    const canonical = MessageTypeHelper.canonicalize(typeStr);
    return canonical !== null && SYSTEM_MESSAGE_TYPES.has(canonical);
  },

  /** Get friendly display content for a message type */
  getFriendlyContent(typeStr: string | null | undefined): string | null {
    const canonical = MessageTypeHelper.canonicalize(typeStr);
    switch (canonical) {
      case "THREAD_CREATED":
        return "Group created";
      case "THREAD_UPDATED":
        return "Group updated";
      case "THREAD_DELETED":
        return "Group deleted";
      case "THREAD_MEMBER_ADDED":
        return "Member added";
      case "THREAD_MEMBER_REMOVED":
        return "Member removed";
      case "EXIT_THREAD":
        return "Left group";
      case "USER_ADDED":
        return "Contact added";
      case "THREAD_KEY":
        return "Encryption keys updated";
      default:
        return null;
    }
  },
};

/** Message status enum */
export const MessageStatus = {
  SENT: "SENT",
  DELIVERED: "DELIVERED",
  READ: "READ",
  FAILED: "FAILED",
} as const;
export type MessageStatus = (typeof MessageStatus)[keyof typeof MessageStatus];

/** Attachment MIME types - matches Rust AttachmentMimeType */
export const AttachmentMimeType = {
  // Documents
  PDF: "application/pdf",
  DOC: "application/msword",
  DOCX: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  XLS: "application/vnd.ms-excel",
  XLSX: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  PPT: "application/vnd.ms-powerpoint",
  PPTX: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  TEXT_PLAIN: "text/plain",
  CSV: "text/csv",
  // Images
  IMAGE_ANY: "image/*",
  JPEG: "image/jpeg",
  PNG: "image/png",
  GIF: "image/gif",
  WEBP: "image/webp",
  SVG: "image/svg+xml",
  BMP: "image/bmp",
  // Audio
  AUDIO_WEBM: "audio/webm",
  AAC: "audio/aac",
  AUDIO_MP4: "audio/mp4",
  // Video
  VIDEO_MP4: "video/mp4",
  VIDEO_MPEG: "video/mpeg",
  QUICKTIME: "video/quicktime",
  AVI: "video/x-msvideo",
  VIDEO_WEBM: "video/webm",
  // Archives
  ZIP: "application/zip",
  RAR: "application/x-rar-compressed",
  SEVEN_ZIP: "application/x-7z-compressed",
  TAR: "application/x-tar",
  GZIP: "application/gzip",
  OCTET_STREAM: "application/octet-stream",
} as const;
export type AttachmentMimeType = (typeof AttachmentMimeType)[keyof typeof AttachmentMimeType];

/** Environment type */
export const Environment = {
  HI: "hi",
  LO: "lo",
  EMPTY: "",
} as const;
export type Environment = (typeof Environment)[keyof typeof Environment];

/** Thread type - explicit classification of thread type */
export const ThreadType = {
  DM: "DM",
  GROUP: "GROUP",
  SELF: "SELF",
} as const;
export type ThreadType = (typeof ThreadType)[keyof typeof ThreadType];

/** Group update reason */
export const GroupUpdateReason = {
  NAME: "NAME",
  CREATE: "CREATE",
  DESCRIPTION: "DESCRIPTION",
  MEMBER_ADDED: "MEMBER_ADDED",
  MEMBER_REMOVED: "MEMBER_REMOVED",
  GROUP_KEY: "GROUP_KEY",
  EXIT_GROUP: "EXIT_GROUP",
} as const;
export type GroupUpdateReason = (typeof GroupUpdateReason)[keyof typeof GroupUpdateReason];

/** Community update reason */
export const CommunityUpdateReason = {
  NAME: "NAME",
  CREATE: "CREATE",
  DESCRIPTION: "DESCRIPTION",
  MEMBER_ADDED: "MEMBER_ADDED",
  MEMBER_REMOVED: "MEMBER_REMOVED",
  COMMUNITY_KEY: "COMMUNITY_KEY",
  EXIT_COMMUNITY: "EXIT_COMMUNITY",
} as const;
export type CommunityUpdateReason = (typeof CommunityUpdateReason)[keyof typeof CommunityUpdateReason];

/** Push notification platform */
export const PushPlatform = {
  IOS_VOIP: "iosvoip",
  IOS: "ios",
  ANDROID: "android",
  WEB: "web",
} as const;
export type PushPlatform = (typeof PushPlatform)[keyof typeof PushPlatform];

/** WebSocket event types (runtime values generated from core bindings) */
export const WebSocketEventType = WebSocketEventTypeValues;
export type WebSocketEventType = WebSocketEventTypeFromBindings;

// ============================================================================
// Additional Types (not generated from Rust)
// ============================================================================

/** Handle parsing result */
export interface ParsedHandle {
  username: string;
  discriminator: string;
}

/** Handler action types returned by core message handlers */
export type HandlerAction =
  | { EnsureContactExists: { user_id: string } }
  | {
      EnsureThreadExists: {
        thread_key: string;
        thread_uuid: string;
        thread_type: string;
        with_user_id?: string | null;
      };
    }
  | { RefreshUserProfile: { user_id: string } }
  | {
      StoreThreadKey: {
        thread_key: string;
        key: string;
        key_version?: number | null;
      };
    }
  | {
      UpdateThreadMetadata: {
        thread_key: string;
        name?: string | null;
        updated_at: number;
      };
    }
  | { AddThreadMember: { thread_key: string; user_id: string } }
  | { RemoveThreadMember: { thread_key: string; user_id: string } }
  | { PersistMessage: { message_id: string; display_content?: string | null } }
  | { SkipPersist: null }
  | { MarkThreadUnread: { thread_id: string } }
  | { ShowNotification: { title: string; body: string; thread_key?: string | null } }
  | { LogDebug: { tag: string; message: string } }
  | { LogWarning: { tag: string; message: string } }
  | { LogError: { tag: string; message: string } };

/** Result returned by core message handlers */
export interface HandleResult {
  actions: HandlerAction[];
  should_persist: boolean;
  friendly_content?: string | null;
  should_notify: boolean;
}
